import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema, tzHeadersSchema, sseQuerySchema } from '../schemas';
import { tradeSchema } from '../../interfaces/ITrade';

// ============================================================
// Query Schemas
// ============================================================

const dateQuerySchema = z.object({
	date: z.string().optional(),
});

const dateRangeQuerySchema = z.object({
	dateFrom: requiredStringSchema,
	dateTo: requiredStringSchema,
});

const professionsProductivityReportsQuerySchema = z.object({
	dateFrom: requiredStringSchema,
	dateTo: requiredStringSchema,
	professionId: z.string().optional(),
});

const singleProfessionProductivityQuerySchema = z.object({
	date: z.string().optional(),
});

const projectProfessionParamsSchema = projectIdParamsSchema.extend({
	professionId: requiredStringSchema,
});

/**
 * `tz` used to live in this query while every sibling route took it in the `tz`
 * header. Harmonised here rather than left alone: the one client that sent it
 * was being edited for the path param anyway, and a route that reads its
 * timezone from a different place than its neighbours is how a report silently
 * renders a day off.
 */
const dailyProductivityForPeriodQuerySchema = z.object({
	dateFrom: requiredStringSchema,
	dateTo: requiredStringSchema,
});

const allTimeProfessionProductivityQuerySchema = z.object({
	professionId: requiredStringSchema,
});

const mainPageActivityDiagnosticQuerySchema = z.object({
	activityId: requiredStringSchema,
});

/**
 * The real trade shape, not `z.looseObject({})`. This is an unauthenticated-by-
 * project, TRUSSTOR_ADMIN write that fans out into every project's denormalised
 * report rows, and until now its body was effectively unvalidated: the handler
 * cast it `as any` and passed it to three `updateTradeInCollection` calls that
 * read `tradeId` off it. A body missing `tradeId` reached Mongo before failing.
 */
const updateTradeBodySchema = tradeSchema;

// ============================================================
// SSE Query Schemas
// ============================================================

const productivityStreamQuerySchema = sseQuerySchema.extend({
	projectId: requiredStringSchema,
	date: requiredStringSchema,
});

// ============================================================
// Contract
// ============================================================

export const professionReportsContract = defineContract(
	'professionReports',
	{
		getProfessionsProductivityV1: route({
			method: 'GET',
			path: '/projects/:projectId/professionsProductivity',
			params: projectIdParamsSchema,
			query: dateQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getProfessionsProductivityV1',
			tags: ['Profession Reports'],
			summary: 'Get daily professions productivity report (v1)',
		}),
		getProfessionsProductivityV2: route({
			method: 'GET',
			path: '/projects/:projectId/professionsProductivity/v2',
			params: projectIdParamsSchema,
			query: dateQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getProfessionsProductivityV2',
			tags: ['Profession Reports'],
			summary: 'Get daily professions productivity report (v2)',
		}),
		getProfessionsProductivityReports: route({
			method: 'GET',
			path: '/projects/:projectId/professionsProductivityReports',
			params: projectIdParamsSchema,
			query: professionsProductivityReportsQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getProfessionsProductivityReports',
			tags: ['Profession Reports'],
			summary: 'Get professions productivity reports between dates',
		}),
		getProfessionProductivity: route({
			method: 'GET',
			path: '/projects/:projectId/professionProductivity/:professionId',
			params: projectProfessionParamsSchema,
			query: singleProfessionProductivityQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getProfessionProductivity',
			tags: ['Profession Reports'],
			summary: 'Get single profession productivity data',
		}),
		getPeriodicProfessionsProductivity: route({
			method: 'GET',
			path: '/projects/:projectId/periodicProfessionsProductivity',
			params: projectIdParamsSchema,
			query: dateRangeQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getPeriodicProfessionsProductivity',
			tags: ['Profession Reports'],
			summary: 'Get periodic professions productivity report',
		}),
		getDailyProfessionsProductivityForPeriod: route({
			method: 'GET',
			path: '/projects/:projectId/dailyProfessionsProductivityForPeriodReport',
			params: projectIdParamsSchema,
			query: dailyProductivityForPeriodQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getDailyProfessionsProductivityForPeriod',
			tags: ['Profession Reports'],
			summary: 'Get daily professions productivity for period',
		}),
		getAllTimeProfessionProductivityStats: route({
			method: 'GET',
			path: '/projects/:projectId/allTimeProfessionProductivityStats',
			params: projectIdParamsSchema,
			query: allTimeProfessionProductivityQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getAllTimeProfessionProductivityStats',
			tags: ['Profession Reports'],
			summary: 'Get all-time profession productivity stats',
		}),
		getProfessionsSafety: route({
			method: 'GET',
			path: '/projects/:projectId/professionsSafety',
			params: projectIdParamsSchema,
			query: dateQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getProfessionsSafety',
			tags: ['Profession Reports'],
			summary: 'Get daily professions safety report',
		}),
		getPeriodicProfessionsSafety: route({
			method: 'GET',
			path: '/projects/:projectId/periodicProfessionsSafety',
			params: projectIdParamsSchema,
			query: dateRangeQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getPeriodicProfessionsSafety',
			tags: ['Profession Reports'],
			summary: 'Get periodic professions safety report',
		}),
		getProfessionsActivities: route({
			method: 'GET',
			path: '/projects/:projectId/professionsActivities',
			params: projectIdParamsSchema,
			query: dateQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getProfessionsActivities',
			tags: ['Profession Reports'],
			summary: 'Get daily professions activities report',
		}),
		getPeriodicActivities: route({
			method: 'GET',
			path: '/projects/:projectId/periodicActivities',
			params: projectIdParamsSchema,
			query: dateRangeQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getPeriodicActivities',
			tags: ['Profession Reports'],
			summary: 'Get periodic activities report',
		}),
		getProfessionsAnalysis: route({
			method: 'GET',
			path: '/projects/:projectId/professionsAnalysis',
			params: projectIdParamsSchema,
			query: dateRangeQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getProfessionsAnalysis',
			tags: ['Profession Reports'],
			summary: 'Get professions analysis report',
		}),
		getSingleProfessionAnalysis: route({
			method: 'GET',
			path: '/projects/:projectId/professionsAnalysis/:professionId',
			params: projectProfessionParamsSchema,
			query: dateRangeQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getSingleProfessionAnalysis',
			tags: ['Profession Reports'],
			summary: 'Get single profession analysis report',
		}),
		getMainPageActivityDaysDiagnostic: route({
			method: 'GET',
			path: '/projects/:projectId/mainPageActivityDaysDiagnosticReport',
			params: projectIdParamsSchema,
			query: mainPageActivityDiagnosticQuerySchema,
			response: z.any(),
			operationId: 'getMainPageActivityDaysDiagnostic',
			tags: ['Profession Reports'],
			summary: 'Get main page activity days diagnostic report',
		}),
		/**
		 * GLOBAL ADMIN, and deliberately not project-scoped. A trade rename has to
		 * reach every project's denormalised report rows —
		 * `professionsProductivity`, `professionsSafety` and
		 * `dailyActivitiesSummaries`, across every project — so there is no single
		 * project that could scope it. Entities drives it on a trade rename, and it
		 * is `requireRole(allowTrusstorAdminRoles)` at the route.
		 */
		updateTrade: route({
			method: 'PUT',
			path: '/updateTrade',
			body: updateTradeBodySchema,
			response: z.string(),
			operationId: 'updateReportsTrade',
			tags: ['Profession Reports'],
			summary: 'Update reports trade (admin only)',
		}),
	},
	{ root: true }
);

// ============================================================
// SSE Route Definitions (standalone — registered via implement())
// ============================================================

export const productivityV1StreamRoute = route({
	method: 'GET',
	path: '/professionsProductivity/stream',
	query: productivityStreamQuerySchema,
	response: z.any(),
	operationId: 'streamProfessionsProductivityV1',
	tags: ['Profession Reports'],
	summary: 'Stream professions productivity report v1 (SSE)',
});

export const productivityV2StreamRoute = route({
	method: 'GET',
	path: '/professionsProductivity/v2/stream',
	query: productivityStreamQuerySchema,
	response: z.any(),
	operationId: 'streamProfessionsProductivityV2',
	tags: ['Profession Reports'],
	summary: 'Stream professions productivity report v2 (SSE)',
});

export const safetyStreamRoute = route({
	method: 'GET',
	path: '/professionsSafety/stream',
	query: productivityStreamQuerySchema,
	response: z.any(),
	operationId: 'streamProfessionsSafety',
	tags: ['Profession Reports'],
	summary: 'Stream professions safety report (SSE)',
});

export const activitiesStreamRoute = route({
	method: 'GET',
	path: '/professionsActivities/stream',
	query: productivityStreamQuerySchema,
	response: z.any(),
	operationId: 'streamProfessionsActivities',
	tags: ['Profession Reports'],
	summary: 'Stream professions activities report (SSE)',
});

export type ProductivityStreamQuery = z.infer<typeof productivityStreamQuerySchema>;
